import { retryIfNot404 } from "@src/global_functions/fortyTwoApi/reactQueryUtils";
import {
  FLOORS_QUERY_KEY,
  tenantsAndSpacesKeys,
} from "@src/global_functions/fortyTwoApi/tenantsAndSpaces/queryKeyFactory";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "@src/global_functions/postgrestApi";
import {
  useMutation,
  useQueryClient,
  useQuery as useReactQuery,
} from "@tanstack/react-query";
import FetchClients from "../client";
import { getBuildingFloors, getBuildingFloorsQueryKey } from "./floors";
import {
  Building,
  BuildingData,
  NotFoundError,
  throwServiceError,
} from "./util";

const building = {
  useQuery,
  useQueryAll,
  mutations: { usePut, useDelete },
} as const;

export default building;

export const BUILDING_QUERY_KEY = "/building";

export const getBuildings = async (site_id: number) => {
  const { data, error } = await FetchClients.tenantsAndSpaces.GET(
    "/{site_id}/building",
    {
      params: { path: { site_id } },
    }
  );
  if (data) return recursiveCamelCaseCipher(data) as BuildingData[];
  throw new Error(error);
};

const _getBuilding = async (site_id: number, building_id: number) => {
  const { data, error } = await FetchClients.tenantsAndSpaces.GET(
    "/{site_id}/building/{building_id}",
    {
      params: { path: { site_id, building_id } },
    }
  );
  if (data) return recursiveCamelCaseCipher(data) as BuildingData;
  throw new Error(error);
};

const getBuilding = async ({
  site_id,
  building_id,
}: {
  site_id: number;
  building_id?: number;
}) => {
  if (!building_id) {
    const [_building] = await getBuildings(site_id);
    if (_building) return _building;
    throw new NotFoundError(`No building set up for site ${site_id}`);
  }
  return _getBuilding(site_id, building_id);
};

function useQuery(
  site_id: number,
  building_id?: number,
  { enabled }: { enabled: boolean } = { enabled: true }
) {
  return useReactQuery({
    queryKey: [BUILDING_QUERY_KEY, site_id, building_id],
    queryFn: () => getBuilding({ site_id, building_id }),
    refetchOnWindowFocus: false,
    retry: retryIfNot404,
    enabled,
  });
}

function useQueryAll(site_id: number) {
  return useReactQuery({
    queryKey: [BUILDING_QUERY_KEY, site_id],
    queryFn: () => getBuildings(site_id),
    refetchOnWindowFocus: false,
  });
}

function usePut(site_id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Building) => {
      const { response, error } = await FetchClients.tenantsAndSpaces.PUT(
        "/{site_id}/building",
        {
          params: { path: { site_id } },
          body: recursiveSnakeCaseCipher(body),
        }
      );
      if (response.ok) return;
      throwServiceError(error);
    },
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({
        queryKey: [BUILDING_QUERY_KEY, site_id],
      });
      await queryClient.invalidateQueries({
        queryKey: getBuildingFloorsQueryKey(site_id, id),
      });
      // invalidate office-specific floors query
      await queryClient.invalidateQueries({
        queryKey: tenantsAndSpacesKeys.query(
          site_id,
          "floor",
          FLOORS_QUERY_KEY
        ),
      });
    },
  });
}

function useDelete(site_id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      blockDeleteIfFloorsExist = false,
      building_id,
    }: {
      blockDeleteIfFloorsExist?: boolean;
      building_id: number;
    }) => {
      if (blockDeleteIfFloorsExist) {
        const { floors } = await getBuildingFloors(site_id, building_id);
        if (floors?.length) {
          throw new Error(
            "Error: delete blocked because floors exist for this building."
          );
        }
      }

      const { response, error } = await FetchClients.tenantsAndSpaces.DELETE(
        "/{site_id}/building/{building_id}",
        {
          params: { path: { site_id, building_id } },
        }
      );
      if (response.ok) return;
      throwServiceError(error);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [BUILDING_QUERY_KEY, site_id],
      });
    },
    onSuccess: async (_, { building_id }) => {
      await queryClient.invalidateQueries({
        queryKey: [BUILDING_QUERY_KEY, site_id],
      });
      queryClient.removeQueries({
        queryKey: getBuildingFloorsQueryKey(site_id, building_id),
      });
    },
  });
}
